// Auth Feature Constants

import type { Permission, Role } from './types';

/**
 * Role to permissions mapping (10 roles — see docs/ROLES_AND_PERMISSIONS_SPEC.md)
 * '*' represents all permissions (super_admin only)
 *
 * Note: Operator permissions are a configurable subset delegated by Owner.
 * Consultant permissions are scoped per-bore per-resource. These defaults
 * represent the maximum permission set for each role; runtime enforcement
 * may further restrict based on borefield-level configuration.
 */
export const ROLE_PERMISSIONS: Record<Role, (Permission | '*')[]> = {
  // Owner: Full CRUD on non-compliance records. Cannot modify compliance records.
  owner: [
    'borefield:read', 'borefield:create', 'borefield:update', 'borefield:delete',
    'bore:read', 'bore:create', 'bore:update', 'bore:delete',
    'job:read', 'job:create', 'job:update', 'job:delete',
    'report:read', 'report:create', 'report:update', 'report:delete',
    'survey:read',
    'rig:read', 'rig:create', 'rig:update', 'rig:delete',
  ],

  // Operator: Configurable subset of Owner (defaults to same as Owner)
  operator: [
    'borefield:read', 'borefield:update',
    'bore:read', 'bore:update',
    'job:read', 'job:create', 'job:update',
    'report:read', 'report:create', 'report:update',
    'survey:read',
    'rig:read',
  ],

  // Designer (A&E): Engineering authority — designs, KPIs, thermal interpretations
  designer: [
    'borefield:read', 'borefield:update',
    'bore:read', 'bore:create', 'bore:update',
    'job:read',
    'report:read',
    'survey:read', 'survey:create', 'survey:update',
    'rig:read',
  ],

  // PM/CM: Operational authority — cost, time, personnel, safety, approval
  pm_cm: [
    'borefield:read', 'borefield:update',
    'bore:read', 'bore:update',
    'job:read', 'job:create', 'job:update',
    'report:read', 'report:create', 'report:update', 'report:delete',
    'survey:read', 'survey:create', 'survey:update',
    'rig:read', 'rig:create', 'rig:update',
  ],

  // Consultant: Need-to-know only — scoped per bore per resource type
  consultant: [
    'borefield:read',
    'bore:read',
    'job:read',
    'report:read',
    'survey:read',
    'rig:read',
  ],

  // Driller / Field Staff: Primary data entry during construction
  driller: [
    'borefield:read',
    'bore:read', 'bore:update',
    'job:read', 'job:create', 'job:update',
    'report:read', 'report:create', 'report:update',
    'survey:read', 'survey:create', 'survey:update',
    'rig:read',
  ],

  // Inspector: Read compliance records, create inspection records only
  inspector: [
    'borefield:read',
    'bore:read',
    'job:read',
    'report:read', 'report:create',
    'survey:read',
    'rig:read',
  ],

  // Field Clerk: Same as Driller (distinct role for customer awareness)
  field_clerk: [
    'borefield:read',
    'bore:read', 'bore:update',
    'job:read', 'job:create', 'job:update',
    'report:read', 'report:create', 'report:update',
    'survey:read', 'survey:create', 'survey:update',
    'rig:read',
  ],

  // Admin: User management only — no project data access by default
  admin: [
    'admin:users', 'admin:settings', 'admin:audit',
  ],

  // Super Admin: OXBO internal — keys to the kingdom
  super_admin: ['*'],
};

/**
 * Role display names
 */
export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  owner: 'Owner',
  operator: 'Operator',
  designer: 'Designer (A&E)',
  pm_cm: 'PM/CM',
  consultant: 'Consultant',
  driller: 'Driller',
  inspector: 'Inspector',
  field_clerk: 'Field Clerk',
  admin: 'Administrator',
  super_admin: 'Super Administrator',
};

/**
 * Role descriptions
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Asset owner — full CRUD on non-compliance records, creates borefields',
  operator: 'Asset operator — configurable subset of Owner permissions',
  designer: 'Engineering authority — bore design, thermal analysis, KPI targets',
  pm_cm: 'Operational authority — cost, time, personnel, safety, approval',
  consultant: 'Need-to-know access scoped per bore per resource type',
  driller: 'Primary data entry during construction — drilling, surveys, reports',
  inspector: 'Third-party verification — read compliance records, create inspections',
  field_clerk: 'Data entry role — same permissions as Driller',
  admin: 'User management and system settings — no project data by default',
  super_admin: 'OXBO internal — full platform access across all tenants',
};

/**
 * Permission descriptions for UI display
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  'borefield:read': 'View borefields',
  'borefield:create': 'Create new borefields',
  'borefield:update': 'Edit borefield details',
  'borefield:delete': 'Delete borefields',
  'bore:read': 'View bores',
  'bore:create': 'Create new bores',
  'bore:update': 'Edit bore details',
  'bore:delete': 'Delete bores',
  'job:read': 'View jobs',
  'job:create': 'Create new jobs',
  'job:update': 'Edit job details',
  'job:delete': 'Delete jobs',
  'report:read': 'View reports',
  'report:create': 'Create new reports',
  'report:update': 'Edit reports',
  'report:delete': 'Delete reports',
  'survey:read': 'View surveys',
  'survey:create': 'Create new surveys',
  'survey:update': 'Edit surveys',
  'survey:delete': 'Delete surveys',
  'rig:read': 'View rigs',
  'rig:create': 'Create new rigs',
  'rig:update': 'Edit rig details',
  'rig:delete': 'Delete rigs',
  'admin:users': 'Manage users',
  'admin:settings': 'Manage system settings',
  'admin:audit': 'View audit logs',
};

/**
 * Session configuration
 */
export const SESSION_CONFIG = {
  // Token refresh threshold (refresh when less than 5 minutes remaining)
  REFRESH_THRESHOLD_MS: 5 * 60 * 1000,
  // Session timeout (30 minutes of inactivity)
  INACTIVITY_TIMEOUT_MS: 30 * 60 * 1000,
  // Token storage key
  TOKEN_STORAGE_KEY: 'auth_token',
  // Refresh token storage key
  REFRESH_TOKEN_STORAGE_KEY: 'auth_refresh_token',
  // User storage key
  USER_STORAGE_KEY: 'auth_user',
};
